import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Newspaper, Send, Pencil, Eye, EyeOff, Archive, ArchiveRestore, RotateCcw, ShieldCheck, Check, X, Settings2, GripVertical, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Field, Btn, DeleteBtn, Loading, Empty, useCrud, handleErr } from "./AdminUI";
import { ImageUpload } from "@/components/ImageUpload";
import { useAuth } from "@/context/AuthContext";
import { NewsBodyEditor } from "./NewsBodyEditor";
import { WriterProfile } from "./WriterProfile";
import { UserCircle } from "lucide-react";

const BLANK = { title: "", summary: "", body: "", category: "", coverUrl: "", author: "", team: "" };
const STATUS = {
  draft: { label: "Draft", cls: "bg-slate-500/15 text-slate-300" },
  pending_verify: { label: "Awaiting verification", cls: "bg-amber-500/15 text-amber-400" },
  pending_approve: { label: "Awaiting approval", cls: "bg-sky-500/15 text-sky-400" },
  rejected: { label: "Rejected", cls: "bg-red-500/15 text-red-400" },
  unpublished: { label: "Unpublished", cls: "bg-orange-500/15 text-orange-400" },
  approved: { label: "Published", cls: "bg-emerald-500/15 text-emerald-400" },
};

function CategoryManager({ onClose, onChanged }) {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const load = async () => { try { const r = await endpoints.adminNewsCategories(); setCats(r.data || []); } catch (e) { handleErr(e); } };
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name.trim()) return; try { await endpoints.adminCreateNewsCategory({ name: name.trim(), order: cats.length, isActive: true }); setName(""); await load(); onChanged?.(); } catch (e) { handleErr(e); } };
  const toggle = async (c) => { try { await endpoints.adminUpdateNewsCategory(c.id, { isActive: c.isActive === false }); await load(); onChanged?.(); } catch (e) { handleErr(e); } };
  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= cats.length) return;
    const a = cats[i], b = cats[j];
    try { await Promise.all([endpoints.adminUpdateNewsCategory(a.id, { order: j }), endpoints.adminUpdateNewsCategory(b.id, { order: i })]); await load(); onChanged?.(); } catch (e) { handleErr(e); }
  };
  const rename = async (c) => { const v = window.prompt("Rename category", c.name); if (!v || v === c.name) return; try { await endpoints.adminUpdateNewsCategory(c.id, { name: v.trim() }); await load(); onChanged?.(); } catch (e) { handleErr(e); } };
  const del = async (c) => { if (!window.confirm(`Delete category "${c.name}"?`)) return; try { await endpoints.adminDeleteNewsCategory(c.id); await load(); onChanged?.(); } catch (e) { handleErr(e); } };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-[#121620] border border-[#273042] p-6">
        <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-bold text-white">Manage news categories</h3><button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button></div>
        <div className="flex gap-2 mb-4">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" data-testid="new-category-input"
            className="flex-1 h-10 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
          <Btn onClick={add} data-testid="add-category-btn"><Plus className="w-4 h-4" /> Add</Btn>
        </div>
        <div className="space-y-2">
          {cats.map((c, i) => (
            <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center gap-2 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 py-2">
              <GripVertical className="w-4 h-4 text-slate-600" />
              <button onClick={() => rename(c)} className="flex-1 text-left text-white text-sm font-medium truncate">{c.name}</button>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${c.isActive === false ? "bg-slate-500/15 text-slate-400" : "bg-emerald-500/15 text-emerald-400"}`}>{c.isActive === false ? "OFF" : "ON"}</span>
              <button onClick={() => move(i, -1)} className="text-slate-400 hover:text-white p-1"><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => move(i, 1)} className="text-slate-400 hover:text-white p-1"><ArrowDown className="w-4 h-4" /></button>
              <button onClick={() => toggle(c)} data-testid={`toggle-category-${c.id}`} className="text-slate-300 hover:text-white text-xs px-2">{c.isActive === false ? "Enable" : "Disable"}</button>
              <button onClick={() => del(c)} data-testid={`delete-category-${c.id}`} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {cats.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No categories yet.</p>}
        </div>
      </div>
    </div>
  );
}

export default function AdminNews() {
  const { can, isMainAdmin } = useAuth();
  const { data: news, loading, reload } = useCrud(endpoints.adminNewsList);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [manageCats, setManageCats] = useState(false);
  const [profile, setProfile] = useState(false);
  const canCreate = isMainAdmin || can("news", "create");
  const canEdit = isMainAdmin || can("news", "edit");
  const canApprove = isMainAdmin || can("news", "approve");
  const canDelete = isMainAdmin;

  const loadCats = () => endpoints.adminNewsCategories().then((r) => setCats(r.data || [])).catch(() => {});
  useEffect(() => { loadCats(); }, []);

  const openNew = () => { setForm({ ...BLANK, category: cats[0]?.name || "" }); setEditingId(null); setOpen(true); };
  const openEdit = (n) => { setForm({ title: n.title || "", summary: n.summary || "", body: n.body || "", category: n.category || "", coverUrl: n.coverUrl || n.thumbnail || "", author: n.author || "", team: n.team || "" }); setEditingId(n.id); setOpen(true); };

  const save = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    try {
      if (editingId) { await endpoints.adminUpdateNews(editingId, form); toast.success("Article updated"); }
      else { await endpoints.adminCreateNews(form); toast.success(isMainAdmin ? "Article published" : "Draft saved — submit for approval"); }
      setForm(BLANK); setOpen(false); setEditingId(null); reload();
    } catch (e) { handleErr(e); }
  };
  const wf = async (id, action) => {
    if (action === "unpublish" && !window.confirm("Unpublish this article? It will be hidden from customers.")) return;
    try { await endpoints.adminWorkflowAction("news", id, action); toast.success("Done"); reload(); } catch (e) { handleErr(e); }
  };
  const submit = (id) => wf(id, "submit");
  const archive = async (n) => {
    if (!n.archived && !window.confirm("Archive this article? It will be hidden from the public News page but kept here.")) return;
    try { await endpoints.adminArchiveNews(n.id, !n.archived); toast.success(n.archived ? "Article restored" : "Article archived"); reload(); } catch (e) { handleErr(e); }
  };
  const teams = [...new Set(news.map((n) => n.team).filter(Boolean))].sort();
  const del = async (id) => { if (!window.confirm("Delete this article?")) return; try { await endpoints.adminDeleteNews(id); toast.success("Deleted"); reload(); } catch (e) { handleErr(e); } };

  return (
    <div>
      <PageHeader title="News" desc={`${news.length} article(s)`}>
        {isMainAdmin && <Btn variant="ghost" onClick={() => setManageCats(true)} data-testid="manage-categories-btn"><Settings2 className="w-4 h-4" /> Categories</Btn>}
        <Btn variant="ghost" onClick={() => setProfile(true)} data-testid="writer-profile-btn"><UserCircle className="w-4 h-4" /> My writer profile</Btn>
        {canCreate && <Btn onClick={openNew} data-testid="admin-add-news-btn"><Newspaper className="w-4 h-4" /> New article</Btn>}
      </PageHeader>

      {open && (
        <Card className="p-5 mb-6 grid gap-3">
          <div className="text-white font-bold text-sm">{editingId ? "Edit article" : "New article"}</div>
          <Field label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-title-input" />
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Category</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="news-category-select"
                className="mt-1.5 w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60">
                <option value="">Select a category…</option>
                {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </label>
            <ImageUpload label="Cover image" value={form.coverUrl} onChange={(url) => setForm({ ...form, coverUrl: url })} testid="news-cover" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Author name (byline)" placeholder="Defaults to your staff name" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="news-author-input" />
            <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Newsroom team / desk</span>
              <input list="news-teams" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} placeholder="e.g. Sports Desk" data-testid="news-team-input"
                className="mt-1.5 w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60" />
              <datalist id="news-teams">{teams.map((t) => <option key={t} value={t} />)}</datalist>
            </label>
          </div>
          <Field label="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="news-summary-input" />
          <NewsBodyEditor value={form.body} onChange={(body) => setForm((f) => ({ ...f, body }))} />
          <div className="flex gap-2">
            <Btn onClick={save} data-testid="news-save-btn">{editingId ? "Save changes" : (isMainAdmin ? "Publish" : "Save draft")}</Btn>
            <Btn variant="ghost" onClick={() => { setOpen(false); setEditingId(null); }}>Cancel</Btn>
          </div>
        </Card>
      )}

      {loading ? <Loading /> : news.length === 0 ? <Empty /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {news.map((n) => {
            const st = STATUS[n.approvalStatus || "approved"] || STATUS.approved;
            const s = n.approvalStatus || "approved";
            const canSubmit = (s === "draft" || s === "rejected") && canCreate;
            return (
              <Card key={n.id} data-testid={`admin-news-${n.id}`} className="overflow-hidden flex flex-col">
                <div className="relative">
                  <img src={n.coverUrl || n.thumbnail || "/bb-logo.png"} alt="" className="w-full aspect-video object-cover" onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
                  <span className={`absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full font-bold ${st.cls}`} data-testid={`news-status-${n.id}`}>{st.label}</span>
                  {n.archived && <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-900/80 text-slate-200" data-testid={`news-archived-${n.id}`}>ARCHIVED</span>}
                </div>
                <div className="p-3 flex flex-col gap-2 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0"><div className="text-white text-sm font-semibold truncate">{n.title}</div><div className="text-xs text-slate-400">{n.category || "Uncategorised"}</div></div>
                    {canDelete && <DeleteBtn onClick={() => del(n.id)} testid={`news-delete-${n.id}`} />}
                  </div>
                  <div className="text-[11px] text-slate-500">by {n.author || n.createdBy?.name || "B&B KIGALI Newsroom"}{n.team ? ` · ${n.team}` : ""}{n.approvedBy ? ` · approved by ${n.approvedBy.name}` : ""}</div>
                  <div className="text-[11px] text-slate-500">{(n.approvedAt || n.createdAt) ? new Date(n.approvedAt || n.createdAt).toLocaleString() : ""}</div>
                  <div className="flex flex-wrap gap-2 mt-auto pt-1">
                    {canEdit && <Btn variant="ghost" onClick={() => openEdit(n)} data-testid={`news-edit-${n.id}`}><Pencil className="w-4 h-4" /> Edit</Btn>}
                    {canSubmit && <Btn onClick={() => submit(n.id)} data-testid={`news-submit-${n.id}`}><Send className="w-4 h-4" /> Submit</Btn>}
                    {canApprove && s !== "approved" && s !== "unpublished" && <Btn onClick={() => wf(n.id, "approve")} data-testid={`news-publish-${n.id}`}><ShieldCheck className="w-4 h-4" /> Publish</Btn>}
                    {canApprove && s === "approved" && <Btn variant="ghost" onClick={() => wf(n.id, "unpublish")} data-testid={`news-unpublish-${n.id}`}><EyeOff className="w-4 h-4" /> Unpublish</Btn>}
                    {canApprove && s === "unpublished" && <Btn onClick={() => wf(n.id, "republish")} data-testid={`news-republish-${n.id}`}><RotateCcw className="w-4 h-4" /> Republish</Btn>}
                    {canApprove && <Btn variant="ghost" onClick={() => archive(n)} data-testid={`news-archive-${n.id}`}>{n.archived ? <><ArchiveRestore className="w-4 h-4" /> Unarchive</> : <><Archive className="w-4 h-4" /> Archive</>}</Btn>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {profile && <WriterProfile onClose={() => setProfile(false)} />}
      {manageCats && <CategoryManager onClose={() => setManageCats(false)} onChanged={loadCats} />}
    </div>
  );
}
